import React from 'react';
import { makeStyles, Typography, Grid, Button, Theme } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    width: '100%',
    border: '2px solid #aaa',
    margin: '1em 0',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    marginBottom: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
    },
  },
  toolTipContainer: {
    display: 'flex',
    alignItems: 'center',
    '& div': {
      display: 'flex',
      alignItems: 'center',
    },
    '& svg': {
      marginLeft: '0.3em',
    },
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
}

export default function DeviceSelectionScreen({ name, roomName }: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const { getToken, isFetching } = useAppState();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;

  const handleJoin = () => {
    getToken(name, roomName).then(({ result }) => {
      videoConnect(result.token);
    });
  };

  if (isFetching || isConnecting) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography align="center" variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  return (
    <>
      {!roomName && (
        <Typography align="center" variant="h5">
          Sala de Reunión ha finalizado
        </Typography>
      )}

      {roomName && (
        <>
          <Typography align="center" variant="h5" className={classes.gutterBottom}>
            Join {roomName}
          </Typography>

          <Grid container justifyContent="center">
            <Grid item xs={12}>
              <div className={classes.localPreviewContainer}>
                <LocalVideoPreview identity={name} />
              </div>

              <Grid container spacing={1}>
                <Grid xs={6} md={4}>
                  <div style={{ width: '100%' }}>
                    <ToggleAudioButton disabled={disableButtons} />
                  </div>
                </Grid>

                <Grid xs={6} md={4}>
                  <div style={{ width: '100%' }}>
                    <ToggleVideoButton disabled={disableButtons} />
                  </div>
                </Grid>

                <Grid xs={12} md={4}>
                  <div style={{ width: '100%' }}>
                    <SettingsMenu />
                  </div>
                </Grid>
              </Grid>
            </Grid>

            <Grid container style={{ marginTop: '24px' }} spacing={1}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  color="primary"
                  data-cy-join-now
                  onClick={handleJoin}
                  disabled={disableButtons}
                  fullWidth
                >
                  Join Now
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button variant="outlined" color="primary" fullWidth disabled={disableButtons}>
                  Cancel
                </Button>
              </Grid>
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <Grid container direction="row" alignItems="center" style={{ marginTop: '1em' }}>
                <Grid item md={5} sm={12} xs={12}>
                  <div className={classes.joinButtons}></div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
